import { useContext, useRef, useState } from "react";

import { getResponseCodes, isResponseOk, useFetch } from "@swa-ui/fetch";

import { AuthContext } from "../../AuthProvider";
import { useUserInfo } from "../../UserInfoProvider";
import Queue from "./Queue";

const API_BASE_PATH_V2 = "/api/mfa/v2/mfa";
const API_TIMEOUT = 30000;
const CIAM_TIER_CODE = "080";

export const useMfaServicesV2 = (onBackgroundError) => {
  const aborted = useRef(false);
  const timedOut = useRef(false);
  const queue = useRef(new Queue({ autostart: true, concurrency: 1 }));
  const fetchOptions = {
    cachePolicy: "no-cache",
    onAbort: () => {
      aborted.current = true;
    },
    onTimeout: () => {
      timedOut.current = true;
    },
    timeout: API_TIMEOUT,
  };
  const { loading: fetchLoading, post, response } = useFetch(API_BASE_PATH_V2, fetchOptions);
  const { post: backgroundPost, response: backgroundResponse } = useFetch(
    API_BASE_PATH_V2,
    fetchOptions
  );
  const {
    auth: { idToken },
  } = useContext(AuthContext);
  const { userInfo } = useUserInfo();
  const [loading, setLoading] = useState(false);
  const ids = useRef({});

  return {
    getMfaDevices,
    loading: fetchLoading || loading,
    resendMfaOtp,
    restartMfa,
    sendMfaOtp,
    validateMfaOtp,
  };

  async function getMfaDevices() {
    const initializeBody = await post("sessions");
    const callStatus = getCallStatus(response, initializeBody);
    let devices = [];

    if (isResponseOk(response)) {
      const { data } = initializeBody;

      ids.current = { connectionId: data.connection_id, id: data.id };

      queue.current.push(async () => {
        const devicesBody = await backgroundPost("devices", getRequestBody({ id_token: idToken }));

        handleBackgroundResponse(
          { callStatus: getCallStatus(backgroundResponse, devicesBody) },
          () => {
            updateCurrentId(devicesBody);
          }
        );
      });

      devices = getPrimaryDeviceOptions();
    }

    return { callStatus, devices };
  }

  async function sendMfaOtp(deviceType) {
    queue.current.push(async () => {
      const body = await backgroundPost("otps", getRequestBody({ method: deviceType }));

      handleBackgroundResponse({ callStatus: getCallStatus(backgroundResponse, body) }, () => {
        updateCurrentId(body);
      });
    });

    await delay(1500);

    return { callStatus: { ok: true } };
  }

  async function resendMfaOtp() {
    const body = await post("otps", getRequestBody());

    updateCurrentId(body);

    return { callStatus: getCallStatus(response, body) };
  }

  async function restartMfa() {
    const body = await post("sessions", getRequestBody());

    updateCurrentId(body);

    return { callStatus: getCallStatus(response, body) };
  }

  async function validateMfaOtp(otp) {
    return new Promise((resolve) => {
      setLoading(true);
      queue.current.push(async () => {
        const body = await post(
          "otp-verifications",
          getRequestBody({
            otp,
          })
        );

        setLoading(false);

        updateCurrentId(body);

        resolve({ callStatus: getCallStatus(response, body) });
      });
    });
  }

  function delay(time) {
    setLoading(true);

    return new Promise((resolve) => {
      setTimeout(() => {
        setLoading(false);
        resolve();
      }, time);
    });
  }

  function getPrimaryDeviceOptions() {
    const primaryPhoneNumber = userInfo["customers.UserInformation.primaryPhoneNumber"];
    const primaryEmail = userInfo["customers.UserInformation.primaryEmail"];

    let deviceOptions = [];

    if (isDomesticPhone(primaryPhoneNumber) || isValidOptionValue(primaryEmail)) {
      deviceOptions = [
        ...getDeviceOption(primaryEmail, "EMAIL", isValidOptionValue, maskEmail),
        ...getDeviceOption(primaryPhoneNumber, "SMS", isDomesticPhone, maskPhoneNumber),
      ];
    } else {
      onBackgroundError?.("useMfaServicesV2__NO_DEVICES_FOUND");
    }

    return deviceOptions;
  }

  function isValidOptionValue(contact) {
    const SAFE_MASKING_SIZE = 5;

    return contact && typeof contact === "string" && contact.length > SAFE_MASKING_SIZE;
  }

  function isDomesticPhone(phoneNumber) {
    return /^\d{10}$/.test(phoneNumber);
  }

  function getDeviceOption(value, deviceType, isValid, mask) {
    return isValid(value)
      ? [
          {
            deviceType,
            value: mask(value),
          },
        ]
      : [];
  }

  function maskPhoneNumber(phoneNumber) {
    let maskedPhoneNumber = phoneNumber;

    if (isValidOptionValue(phoneNumber)) {
      maskedPhoneNumber = `${phoneNumber.slice(0, -4).replace(/\d/g, "*")}${phoneNumber.slice(-4)}`;
    }

    return maskedPhoneNumber;
  }

  function maskEmail(email) {
    let maskedEmail = email;

    if (isValidOptionValue(email)) {
      const [user, domain] = email.split("@");
      const firstCharacter = user.slice(0, 1);
      const lastCharacter = user.slice(-1);
      const maskedUserWithoutFirstAndLast = user.slice(1, -1).replace(/\S/g, "*");

      maskedEmail = `${firstCharacter}${maskedUserWithoutFirstAndLast}${lastCharacter}@${domain}`;
    }

    return maskedEmail;
  }

  function getRequestBody(parameters = {}) {
    return {
      connection_id: ids.current.connectionId,
      id: ids.current.id,
      ...parameters,
    };
  }

  function getCallStatus(serviceResponse, body) {
    const errorCodes = body ? getResponseCodes(body) : [];
    const errorCode = isResponseOk(serviceResponse)
      ? undefined
      : errorCodes?.[0] ?? `${serviceResponse.status ?? "000"}${CIAM_TIER_CODE}000`;

    return {
      aborted: aborted.current,
      errorCode,
      ok: !errorCode && !aborted.current,
      requestId: serviceResponse?.data?.requestId,
      timedOut: timedOut.current,
    };
  }

  function updateCurrentId(body) {
    ids.current.id = body && body?.data?.id;
  }

  function handleBackgroundResponse(
    responseToHandle,
    successCallback = undefined,
    errorCallback = undefined
  ) {
    const { callStatus, ...data } = responseToHandle;

    if (callStatus.ok) {
      successCallback && successCallback(data);
    } else {
      errorCallback ? errorCallback(callStatus) : onBackgroundError?.(callStatus);
    }
  }
};
